import { useCallback, useEffect, useRef } from 'react';
import { APIGetUnreadCounts, createNotificationWebSocketConnection } from '../api';

// Один сокет на Layout, все кнопки читают одну карту. Событие — повод сверить БД.
export function usePersonalChatRealtime({ token, actorId, setSnapshot, onNotification, onStatus }) {
  const handlers = useRef({ onNotification, onStatus });
  handlers.current = { onNotification, onStatus };
  const refreshRef = useRef(async () => {});
  const refresh = useCallback(() => refreshRef.current(), []);
  useEffect(() => {
    let disposed = false, running = false, dirty = false, timer = null;
    setSnapshot({ unread_counts: [], timestamp: 0 });
    if (!token || !actorId) {
      handlers.current.onStatus?.(false);
      return undefined;
    }
    const fetchCounts = async () => {
      dirty = true;
      if (running || disposed) return;
      running = true;
      try {
        while (dirty && !disposed) {
          dirty = false;
          const data = await APIGetUnreadCounts(token);
          if (disposed) break;
          // Если за время запроса пришло новое событие, дождёмся следующего снимка.
          if (dirty) continue;
          setSnapshot({ unread_counts: data?.unread_counts || [], timestamp: Date.now() });
          window.dispatchEvent(new CustomEvent('planium:unread-synced'));
        }
      } catch (error) {
        if (!disposed) console.error('Не удалось сверить непрочитанные', error);
      } finally { running = false; }
    };
    refreshRef.current = fetchCounts;
    const schedule = () => {
      dirty = true;
      if (timer !== null) return;
      timer = setTimeout(() => { timer = null; fetchCounts(); }, 150);
    };
    const connection = createNotificationWebSocketConnection(token, data => {
      if (disposed) return;
      if (['unread_changed', 'initial_data', 'new_message', 'message_read', 'new_chat'].includes(data?.type)) schedule();
      // initial_data не перезаписывает более свежий HTTP-снимок.
      if (!['unread_changed', 'initial_data'].includes(data?.type)) handlers.current.onNotification?.(data);
    }, connected => {
      if (disposed) return;
      handlers.current.onStatus?.(connected);
      if (connected) schedule();
    });
    const wake = () => {
      if (document.visibilityState === 'visible') schedule();
    };
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    // Страховка потерь channel layer, включая тихий обрыв при работающем heartbeat.
    const poll = setInterval(wake, 60000);
    fetchCounts();
    return () => {
      disposed = true;
      clearTimeout(timer); clearInterval(poll);
      connection.close();
      refreshRef.current = async () => {};
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [token, actorId, setSnapshot]);
  return refresh;
}